/**
 * The optimize request body, shared by the Optimize tab (manual runs) and
 * scheduled runs, so both send the same thing for the same parameters.
 *
 * OPTIMIZE_DEFAULTS is the single source of the parameter defaults. A new
 * optimize parameter is added here, to buildOptimizeRequestBody, and to the
 * schedule settings schema (optimizeScheduleSettings.js); the schedule tests
 * fail until all three agree.
 */
import { DEFAULT_MIN_FREQ_GAP_MHZ, DEFAULT_NEIGHBOR_RSSI_FLOOR_DBM } from './channelMapUtils';
import { parseTreemgmtChannelList } from './treemgmtChannelUtils';
import { buildOptimizeScopePayload } from './optimizeScopeUtils';

/** Sent as same_chassis_min_frequency_separation_mhz: backend uses the per-role floors. */
export const OPTIMIZE_GLOBAL_SAME_CHASSIS_MIN_SEP_MHZ = -1;

export const OPTIMIZE_DEFAULTS = Object.freeze({
  // General
  maxIterations: 3,
  useIperf: true,
  useHealthFocus: false,
  rebuildTree: false,
  // Topology
  improvementFloor: 1.0,
  reparentWhitelistCount: 3,
  // Power
  powerImprovementFloorDb: 3,
  powerUplinkSeverityWeight: 1.0,
  powerRegulatoryDomain: 'default',
  powerCapAntennaGainDbi: 11,
  powerNodeAntennaGainDbi: 0,
  // Channels
  regulatoryDomain: 'default',
  capChannelBandwidth: 40,
  hopChannelBandwidth: 20,
  minFrequencySeparationMHz: DEFAULT_MIN_FREQ_GAP_MHZ,
  capSameChassisMinSep: 40,
  hopSameChassisMinSep: 20,
  threshold: 0.3,
  channelGlobalAcceptFloor: 0.05,
  channelImprovementFloor: 0.05,
  channelStickyCooldownMin: 60,
  neighborRssiFloorDbm: DEFAULT_NEIGHBOR_RSSI_FLOOR_DBM,
  capOnlyActions: false,
  excludeIdleAPs: true,
  fixChannelGroups: true,
  segmentAware: true,
  excludePublicWifiRadios: true,
  excludeMaxChildrenZeroRadios: true,
});

/**
 * Build the optimize request body from parameter values (OPTIMIZE_DEFAULTS
 * keys) plus scope, channel allow-lists, steps, iperf run and debug.
 * Tunables are only sent when they differ from the default; backend-on
 * toggles only when turned off. Returns { body, enabledSteps } or { error }.
 */
export function buildOptimizeRequestBody(p) {
  const {
    scopeMode = 'site',
    selectedCapSerials = [],
    selectedCustomTreeIds = [],
    mapFocusSerials = [],
    channelGroupIds = [],
    channelAllowList5GHz = '',
    channelAllowList6GHz = '',
    enableTopology = true,
    enablePower = true,
    enableChannel = true,
    iperfRunId = '',
    debug = false,
  } = p;
  const v = { ...OPTIMIZE_DEFAULTS, ...p };
  const d = OPTIMIZE_DEFAULTS;

  if (scopeMode === 'trees' && selectedCustomTreeIds.length === 0) {
    return { error: 'Select at least one custom tree in Optimize scope.' };
  }
  if (scopeMode === 'caps' && selectedCapSerials.length === 0) {
    return { error: 'Select at least one CAP in Optimize scope.' };
  }

  let channelAllowLists;
  if (String(channelAllowList5GHz).trim() || String(channelAllowList6GHz).trim()) {
    channelAllowLists = {};
    if (String(channelAllowList5GHz).trim()) {
      const parsed5 = parseTreemgmtChannelList(channelAllowList5GHz);
      if (parsed5.error) {
        return { error: `5 GHz channel list: ${parsed5.error}` };
      }
      channelAllowLists['5GHz'] = parsed5.channels;
    }
    if (String(channelAllowList6GHz).trim()) {
      const parsed6 = parseTreemgmtChannelList(channelAllowList6GHz);
      if (parsed6.error) {
        return { error: `6 GHz channel list: ${parsed6.error}` };
      }
      channelAllowLists['6GHz'] = parsed6.channels;
    }
  }

  const enabledSteps = [];
  if (enableTopology) enabledSteps.push('topology');
  if (enablePower) enabledSteps.push('power');
  if (enableChannel) enabledSteps.push('channel');
  const body = {
    max_iterations: v.maxIterations,
    use_health_focus: v.useHealthFocus,
    rebuild_tree: v.rebuildTree,
    channel_bandwidth: Math.max(v.capChannelBandwidth, v.hopChannelBandwidth),
    threshold: v.threshold,
    min_frequency_separation_mhz: v.minFrequencySeparationMHz,
    same_chassis_min_frequency_separation_mhz: OPTIMIZE_GLOBAL_SAME_CHASSIS_MIN_SEP_MHZ,
    channel_global_accept_floor: v.channelGlobalAcceptFloor,
    channel_stickiness_cooldown_minutes: v.channelStickyCooldownMin,
    neighbor_rssi_floor_dbm: v.neighborRssiFloorDbm,
    regulatory_domain: v.regulatoryDomain,
    cap_only_actions: v.capOnlyActions,
    ...(v.excludeIdleAPs ? {} : { idle_ap_retry_disabled: true }),
    use_iperf: v.useIperf,
    iperf_run_id: v.useIperf ? iperfRunId : '',
    // Empty array = all steps (backend default). Sending specific
    // steps restricts the loop to only those.
    enabled_steps: enabledSteps.length === 3 ? [] : enabledSteps,
    debug,
    ...(v.improvementFloor !== d.improvementFloor
      && Number.isFinite(v.improvementFloor)
      && v.improvementFloor > 0
      ? { improvement_floor: v.improvementFloor }
      : {}),
    // Power tunables. Only sent when they differ from the backend
    // defaults so the response stays a no-op for operators that
    // didn't touch the form.
    ...(v.powerImprovementFloorDb !== d.powerImprovementFloorDb
      && Number.isFinite(v.powerImprovementFloorDb)
      && v.powerImprovementFloorDb >= 0
      ? { power_improvement_floor_db: v.powerImprovementFloorDb }
      : {}),
    ...(v.powerUplinkSeverityWeight !== d.powerUplinkSeverityWeight
      && Number.isFinite(v.powerUplinkSeverityWeight)
      && v.powerUplinkSeverityWeight > 0
      ? { power_uplink_severity_weight: v.powerUplinkSeverityWeight }
      : {}),
    ...(v.powerRegulatoryDomain && v.powerRegulatoryDomain !== 'default'
      ? { power_regulatory_domain: v.powerRegulatoryDomain }
      : {}),
    ...(v.powerCapAntennaGainDbi !== d.powerCapAntennaGainDbi
      && Number.isFinite(v.powerCapAntennaGainDbi)
      ? { power_cap_antenna_gain_dbi: v.powerCapAntennaGainDbi }
      : {}),
    ...(v.powerNodeAntennaGainDbi !== d.powerNodeAntennaGainDbi
      && Number.isFinite(v.powerNodeAntennaGainDbi)
      ? { power_node_antenna_gain_dbi: v.powerNodeAntennaGainDbi }
      : {}),
    // Channel improvement floor: chained-mode per-action gate.
    ...(v.channelImprovementFloor !== d.channelImprovementFloor
      && Number.isFinite(v.channelImprovementFloor)
      && v.channelImprovementFloor >= 0
      ? { channel_improvement_floor: v.channelImprovementFloor }
      : {}),
    ...(v.reparentWhitelistCount !== d.reparentWhitelistCount
      && Number.isFinite(v.reparentWhitelistCount)
      && v.reparentWhitelistCount >= 1
      ? { reparent_whitelist_count: v.reparentWhitelistCount }
      : {}),
    ...(scopeMode === 'trees' && selectedCustomTreeIds.length === 1 ? { custom_tree_id: selectedCustomTreeIds[0] } : {}),
    ...(scopeMode === 'trees' && selectedCustomTreeIds.length > 1 ? { custom_tree_ids: [...selectedCustomTreeIds] } : {}),
    ...(channelGroupIds.length > 0 ? { channel_group_ids: [...channelGroupIds] } : {}),
    ...(channelAllowLists ? { channel_allow_lists: channelAllowLists } : {}),
    // Per-role channel planning. Per-role bandwidth / same-chassis
    // floors are only sent when they differ from the role defaults;
    // the feature toggles are only sent when turned OFF (backend
    // defaults them ON).
    ...(v.capChannelBandwidth !== d.capChannelBandwidth ? { cap_channel_bandwidth: v.capChannelBandwidth } : {}),
    ...(v.hopChannelBandwidth !== d.hopChannelBandwidth ? { hop_channel_bandwidth: v.hopChannelBandwidth } : {}),
    ...(v.capSameChassisMinSep !== d.capSameChassisMinSep
      ? { cap_same_chassis_min_frequency_separation_mhz: v.capSameChassisMinSep }
      : {}),
    ...(v.hopSameChassisMinSep !== d.hopSameChassisMinSep
      ? { hop_same_chassis_min_frequency_separation_mhz: v.hopSameChassisMinSep }
      : {}),
    ...(v.fixChannelGroups ? {} : { fix_channel_groups: false }),
    ...(v.segmentAware ? {} : { segment_aware: false }),
    ...(v.excludePublicWifiRadios ? {} : { exclude_public_wifi_radios: false }),
    ...(v.excludeMaxChildrenZeroRadios ? {} : { exclude_max_children_zero_radios: false }),
    ...buildOptimizeScopePayload(scopeMode, { selectedCapSerials: [...selectedCapSerials], mapFocusSerials: [...mapFocusSerials] }),
  };
  return { body, enabledSteps };
}
